import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  getMerchantProfile, 
  createMerchantProfile, 
  listPaymentButtons,
  getMerchantPayments,
  getMerchantAnalytics,
  getPayoutBalance,
  getPayoutSchedule,
  getPayoutHistory
} from '../lib/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Store,
  Plus,
  CreditCard,
  Link as LinkIcon,
  Code,
  Loader2,
  CheckCircle,
  AlertCircle,
  Wallet,
  Users,
  FileText,
  Webhook
} from 'lucide-react';

import {
  MerchantStats,
  MerchantPayoutsTab,
  MerchantTransactionsTab,
  MerchantButtonsTab,
  MerchantIntegrationTab,
  MerchantSettingsTab,
  MerchantCustomersTab,
  MerchantInvoicesTab,
  MerchantWebhooksTab
} from '../components/merchant';

const BUSINESS_TYPES = [
  { value: 'retail', label: 'Retail' },
  { value: 'ecommerce', label: 'E-commerce' },
  { value: 'services', label: 'Services' },
  { value: 'food', label: 'Food & Beverage' },
  { value: 'other', label: 'Other' }
];

const MerchantDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [buttons, setButtons] = useState([]);
  const [payments, setPayments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createProfileOpen, setCreateProfileOpen] = useState(false);
  
  // Payout states
  const [payoutBalance, setPayoutBalance] = useState(null);
  const [payoutSchedule, setPayoutSchedule] = useState(null);
  const [payoutHistory, setPayoutHistory] = useState([]);
  
  // Form states
  const [profileForm, setProfileForm] = useState({
    business_name: '',
    business_type: 'ecommerce',
    website: '',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const profileRes = await getMerchantProfile();
      setProfile(profileRes.data);
      
      const [buttonsRes, paymentsRes, analyticsRes] = await Promise.all([
        listPaymentButtons(),
        getMerchantPayments(),
        getMerchantAnalytics()
      ]);
      
      setButtons(buttonsRes.data);
      setPayments(paymentsRes.data);
      setAnalytics(analyticsRes.data);
      
      // Load payout data
      try {
        const [balanceRes, historyRes] = await Promise.all([
          getPayoutBalance(),
          getPayoutHistory()
        ]);
        setPayoutBalance(balanceRes.data);
        setPayoutHistory(historyRes.data);
        
        // Try to get schedule (may not exist)
        try {
          const scheduleRes = await getPayoutSchedule();
          setPayoutSchedule(scheduleRes.data);
        } catch {
          setPayoutSchedule(null);
        }
      } catch {
        logger.log('Payout data not available');
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to load merchant data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    if (!profileForm.business_name) {
      toast.error('Business name is required');
      return;
    }
    
    try {
      const res = await createMerchantProfile(profileForm);
      setProfile(res.data);
      setCreateProfileOpen(false);
      toast.success('Merchant profile created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  // No profile - show setup
  if (!profile) {
    return (
      <Layout>
        <motion.div 
          className="max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="text-center py-12">
            <CardContent className="space-y-6">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-500/10 flex items-center justify-center">
                <Store className="w-8 h-8 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold">Start Accepting Payments</h2>
                <p className="text-muted-foreground mt-2">
                  Create your merchant profile to accept payments on your website
                </p>
              </div>
              
              <Dialog open={createProfileOpen} onOpenChange={setCreateProfileOpen}>
                <DialogTrigger asChild>
                  <Button size="lg" className="rounded-xl" data-testid="create-merchant-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Create Merchant Profile
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Create Merchant Profile</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateProfile} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="business_name">Business Name *</Label>
                      <Input
                        id="business_name"
                        value={profileForm.business_name}
                        onChange={(e) => setProfileForm({ ...profileForm, business_name: e.target.value })}
                        placeholder="Your Business Name"
                        data-testid="business-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="business_type">Business Type</Label>
                      <select
                        id="business_type"
                        value={profileForm.business_type}
                        onChange={(e) => setProfileForm({ ...profileForm, business_type: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                        data-testid="business-type-select"
                      >
                        {BUSINESS_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>{type.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website">Website</Label>
                      <Input
                        id="website"
                        type="url"
                        value={profileForm.website}
                        onChange={(e) => setProfileForm({ ...profileForm, website: e.target.value })}
                        placeholder="https://yourwebsite.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Input
                        id="description"
                        value={profileForm.description}
                        onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                        placeholder="Brief description of your business"
                      />
                    </div>
                    <Button type="submit" className="w-full rounded-xl" data-testid="submit-profile-btn">
                      Create Profile
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </CardContent>
          </Card>
        </motion.div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div 
        className="space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="merchant-dashboard"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <Store className="w-8 h-8 text-emerald-600" />
              Merchant Dashboard
            </h1>
            <p className="text-muted-foreground">{profile.business_name}</p>
          </div>
          <div className="flex items-center gap-2">
            {profile.is_verified ? (
              <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 text-sm">
                <CheckCircle className="w-4 h-4" />
                Verified
              </span>
            ) : (
              <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-orange-500/10 text-orange-600 text-sm">
                <AlertCircle className="w-4 h-4" />
                Pending Verification
              </span>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <MerchantStats analytics={analytics} />

        <Tabs defaultValue="buttons" className="w-full">
          <TabsList className="grid w-full grid-cols-8 h-12 rounded-xl">
            <TabsTrigger value="buttons" className="rounded-lg text-xs">
              <LinkIcon className="w-4 h-4 mr-1" />
              Buttons
            </TabsTrigger>
            <TabsTrigger value="transactions" className="rounded-lg text-xs">
              <CreditCard className="w-4 h-4 mr-1" />
              Transactions
            </TabsTrigger>
            <TabsTrigger value="customers" className="rounded-lg text-xs">
              <Users className="w-4 h-4 mr-1" />
              Customers
            </TabsTrigger>
            <TabsTrigger value="invoices" className="rounded-lg text-xs">
              <FileText className="w-4 h-4 mr-1" />
              Invoices
            </TabsTrigger>
            <TabsTrigger value="payouts" className="rounded-lg text-xs">
              <Wallet className="w-4 h-4 mr-1" />
              Payouts
            </TabsTrigger>
            <TabsTrigger value="webhooks" className="rounded-lg text-xs">
              <Webhook className="w-4 h-4 mr-1" />
              Webhooks
            </TabsTrigger>
            <TabsTrigger value="integration" className="rounded-lg text-xs">
              <Code className="w-4 h-4 mr-1" />
              API
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-lg text-xs">
              <Store className="w-4 h-4 mr-1" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="buttons">
            <MerchantButtonsTab buttons={buttons} setButtons={setButtons} />
          </TabsContent>

          <TabsContent value="transactions">
            <MerchantTransactionsTab payments={payments} />
          </TabsContent>

          <TabsContent value="customers">
            <MerchantCustomersTab />
          </TabsContent>

          <TabsContent value="invoices">
            <MerchantInvoicesTab />
          </TabsContent>

          <TabsContent value="payouts">
            <MerchantPayoutsTab 
              payoutBalance={payoutBalance}
              setPayoutBalance={setPayoutBalance}
              payoutSchedule={payoutSchedule}
              setPayoutSchedule={setPayoutSchedule}
              payoutHistory={payoutHistory}
              setPayoutHistory={setPayoutHistory}
            />
          </TabsContent>

          <TabsContent value="webhooks">
            <MerchantWebhooksTab />
          </TabsContent>

          <TabsContent value="integration">
            <MerchantIntegrationTab profile={profile} setProfile={setProfile} />
          </TabsContent>

          <TabsContent value="settings">
            <MerchantSettingsTab profile={profile} />
          </TabsContent>
        </Tabs>
      </motion.div>
    </Layout>
  );
};

export default MerchantDashboard;
